// src/stores/useDashboardStore.js
import { defineStore } from "pinia";
import dashboardService from "@/services/dashboardService";
import api from "@/services/api";
import { useUserStore } from "./useUserStore";

// 🔹 Helper de mídia servida pela API (avatar, capa de texto)
// O banco guarda caminho relativo; o arquivo vem do wwwroot da API, que fica
// noutra origem que a do front. Daí montar a URL a partir do baseURL do axios,
// sem o sufixo /api.
function buildMediaUrl(relativePath) {
  let path = relativePath || "";
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");
  if (!path.startsWith("/")) path = "/" + path;

  return base + path;
}

/**
 * 🔹 Normaliza datas vindas do backend
 * Resolve problemas de fuso horário tratando a string como local se não houver offset.
 */
export function normalizeBackendDate(raw) {
  if (!raw) return null;

  let s = String(raw).trim();

  // "2026-02-11 10:00:00" -> "2026-02-11T10:00:00"
  if (s.includes(" ") && !s.includes("T")) {
    s = s.replace(" ", "T");
  }

  // Remove o 'Z' para que o Date() do navegador não subtraia o fuso local (Ex: -3h)
  if (s.endsWith("Z")) {
    s = s.slice(0, -1);
  }

  return s;
}

// 🔹 Decide o "range" do chart a partir de monthsForChart (mantém seu parâmetro sem quebrar chamadas)
function monthsToRange(monthsForChart) {
  const n = Number(monthsForChart);

  // se o dev passar 1 ou 0, geralmente quer algo mais curto (dia)
  if (!Number.isFinite(n)) return "month";
  if (n <= 1) return "day";

  // se passar 12+ pode fazer sentido usar "year"
  if (n >= 12) return "year";

  // default: month
  return "month";
}

export const useDashboardStore = defineStore("dashboard", {
  state: () => ({
    userStats: {
      texts: 0,
      activities: 0,
      notes: 0,
      followers: 0,
      following: 0,
    },
    lastAnnotatedTexts: [],
    followedActivities: [],
    activeUsers: [],
    rankingByNotes: [],
    notesByMonth: [],
    isLoading: false,
    error: null,
  }),

  actions: {
    async fetchDashboard({
      lastTextsLimit = 20,
      followedLimit = 20,
      activeUsersLimit = 20,
      rankingLimit = 20,
      monthsForChart = 6, // mantido por compatibilidade com o seu front
    } = {}) {
      this.isLoading = true;
      this.error = null;

      try {
        const userStore = useUserStore();
        const currentUserId = userStore.currentUser?.userId;

        // ✅ Controller/Service agora usa: getNotesChart(range)
        const chartRange = monthsToRange(monthsForChart);

        const [
          stats,
          lastTexts,
          followed,
          active,
          ranking,
          notesChart,
          followersCount,
          followingCount,
        ] = await Promise.all([
          dashboardService.getUserStats(),
          dashboardService.getLastAnnotatedTexts(lastTextsLimit),
          dashboardService.getFollowedUsersLastActions(followedLimit),
          dashboardService.getActiveUsersLastActions(activeUsersLimit),
          dashboardService.getNotesRanking(rankingLimit),
          dashboardService.getNotesChart(chartRange),
          currentUserId ? dashboardService.getFollowersCount(currentUserId) : 0,
          currentUserId ? dashboardService.getFollowingCount(currentUserId) : 0,
        ]);

        // 🔹 Estatísticas do usuário
        this.userStats = {
          texts: stats?.totalTexts ?? stats?.texts ?? 0,
          activities: stats?.totalActivities ?? stats?.activities ?? 0,
          notes: stats?.totalNotes ?? stats?.notes ?? 0,
          followers: followersCount,
          following: followingCount,
        };

        // 🔹 Últimos textos anotados
        const safeLastTexts = Array.isArray(lastTexts) ? lastTexts : [];
        this.lastAnnotatedTexts = safeLastTexts.map((t) => ({
          id: t.textId || t.id,
          userId: t.lastNoteUserId ?? t.createdByUserId ?? t.userId ?? null,
          userName: t.lastNoteUserName ?? t.createdByUserName ?? t.userName ?? null,
          createdByUserId: t.createdByUserId ?? t.userId ?? null,
          title: t.title,
          subtitle: t.subtitle,
          // Capa enviada para o texto, resolvida aqui como os avatares: é o
          // store que devolve URL pronta nesta tela. Fica null quando o texto
          // não tem imagem — aí a lista desenha a capa de reserva.
          cover: buildMediaUrl(t.coverPath),
          author: t.createdByUserName || t.author,
          noteCount: t.totalNotes ?? t.noteCount ?? 0,
          // ✅ NOVO: contadores granulares vindos do backend
          myNotes: t.myNotes ?? 0,
          otherNotes: t.otherNotes ?? 0,
          // ✅ NOVO: assuntos do texto (chips no dashboard). Índices paralelos:
          // topicNames[i] corresponde a topicIds[i].
          topicIds: Array.isArray(t.topicIds) ? t.topicIds : [],
          topicNames: Array.isArray(t.topicNames) ? t.topicNames : [],
          activity: t.activityTitle ?? t.activity ?? "",
          // Série em que a última nota foi postada — é ela que o clique no
          // título abre. Fica null quando a API ainda não manda o campo, e é
          // esse null que faz o dashboard cair no comportamento antigo em vez
          // de navegar para uma atividade inexistente.
          lastNoteActivityId: t.lastNoteActivityId ?? null,
          lastNoteAt: normalizeBackendDate(t.lastNoteCreatedAt),
          avatar: buildMediaUrl(
            t.lastNoteUserPhotoPath ||
              t.createdByUserPhotoPath ||
              t.avatarPath ||
              t.photoPath,
          ),
        }));

        // 🔹 Atividades de quem você segue
        this.followedActivities = (Array.isArray(followed) ? followed : []).map(
          (a) => ({
            id: a.logId ?? `${a.userId}-${a.occurredAt}`,
            userId: a.userId,
            // Exibição usa o username; o fullName vai como hint no avatar.
            userName: a.userName,
            fullName: a.fullName ?? "",

            action: a.action ?? "",
            lastAction: a.action ?? a.lastActionDescription ?? "",

            resourceType: a.resourceType ?? "",
            resourceName: a.resourceName ?? "",

            resourceId: a.resourceId ?? a.ResourceId ?? null,
            relatedTextId: a.relatedTextId ?? a.RelatedTextId ?? null,
            relatedActivityId:
              a.relatedActivityId ?? a.RelatedActivityId ?? null,

            relatedTextName: a.relatedTextName ?? a.RelatedTextName ?? "",
            relatedActivityName:
              a.relatedActivityName ?? a.RelatedActivityName ?? "",

            when: normalizeBackendDate(a.occurredAt),
            avatar: buildMediaUrl(a.photoPath),
          }),
        );

        // 🔹 Últimos usuários ativos
        this.activeUsers = (Array.isArray(active) ? active : []).map((u) => ({
          id: u.userId,
          // Exibição usa o username; o fullName vai como hint no avatar.
          name: u.userName,
          fullName: u.fullName ?? "",

          action: u.action ?? "",
          lastAction: u.action ?? u.lastActionDescription ?? "",

          resourceType: u.resourceType ?? "",
          resourceName: u.resourceName ?? "",

          resourceId: u.resourceId ?? u.ResourceId ?? null,
          relatedTextId: u.relatedTextId ?? u.RelatedTextId ?? null,
          relatedActivityId: u.relatedActivityId ?? u.RelatedActivityId ?? null,

          relatedTextName: u.relatedTextName ?? u.RelatedTextName ?? "",
          relatedActivityName:
            u.relatedActivityName ?? u.RelatedActivityName ?? "",

          when: normalizeBackendDate(u.occurredAt),
          avatar: buildMediaUrl(u.photoPath),
        }));

        // 🔹 Ranking
        this.rankingByNotes = (Array.isArray(ranking) ? ranking : []).map(
          (r, index) => ({
            position: r.position ?? index + 1,
            id: r.userId,
            // Exibição usa o username; o fullName vai como hint no avatar.
            name: r.userName,
            fullName: r.fullName ?? "",
            notesCount: r.totalNotes ?? r.notesCount ?? 0,
            isCurrentUser: !!r.isCurrentUser,
            avatar: buildMediaUrl(r.photoPath),
          }),
        );

        // 🔹 Gráfico (compatível com GET /Dashboard/notes-chart)
        // Aqui eu NÃO invento esquema: eu só tento mapear o que vier.
        // Se vier no formato { label, value } já passa.
        // Se vier { monthLabel, totalNotes } também passa.
        const safeChart = Array.isArray(notesChart) ? notesChart : [];
        this.notesByMonth = safeChart.map((p) => ({
          label:
            p.label ??
            p.monthLabel ??
            p.dayLabel ??
            p.yearLabel ??
            (p.month != null && p.year != null
              ? `${String(p.month).padStart(2, "0")}/${p.year}`
              : String(p.period ?? p.key ?? "")),
          value: p.value ?? p.totalNotes ?? p.notesCount ?? 0,
        }));
      } catch (err) {
        console.error("❌ Erro ao carregar dashboard:", err);
        this.error =
          err?.response?.data?.message || "Erro ao carregar dashboard.";
      } finally {
        this.isLoading = false;
      }
    },
  },
});
